import React, { useEffect } from 'react'
import ProgramEditingSubSection from './Components/ProgramEditingSubSection'
import ProgramOption from './Components/ProgramOption'
import ProgramPriceCoverage from 'core-system/Program/ProgramPriceCoverage'
import FlexUtils from 'features/Flex/Shared/FlexUtils'
import FormattingUtils from 'shared/FormattingUtils'
import { getAllEmployeeSegment } from '../Shared/helperFunctions'
import BudgetUsesSelector from 'features/Programs/Editing/Components/BudgetUsesSelector'
import { Segment } from 'redux/employeePlatform/employeePlatformTypes'
import {
  flexType,
  flexActiveOptionsType,
  availableFlexMerchant,
} from '../Shared/types'
import { FLEX_INITIAL_STATE } from './ProgramEditingSlice'
import Radio from 'core-system/Radio'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import ProgramSectionSubHeading from './Components/ProgramSectionSubHeading'

interface FlexProgramEditingProps {
  flex: flexType
  availableMerchants: availableFlexMerchant
  getFlexRecommendations: (segmentId: string) => void
  changeFlexActiveMerchantIds: (
    type: flexActiveOptionsType,
    value: string[]
  ) => void
  changeFlexActiveOptions: (type: flexActiveOptionsType, value: boolean) => void
  changeFlexBudget: (budget: number) => void
  changeFlexBudgetCycle: (cycle: 'daily' | 'monthly') => void
  isContinued: boolean
  allSegments: Segment[]
}

const FlexProgramEditing = ({
  flex = FLEX_INITIAL_STATE,
  availableMerchants,
  getFlexRecommendations,
  changeFlexActiveMerchantIds,
  changeFlexActiveOptions,
  changeFlexBudget,
  changeFlexBudgetCycle,
  isContinued,
  allSegments,
}: FlexProgramEditingProps) => {
  const flexMerchantIds = availableMerchants?.flex.map((m) => m.id) || []

  useEffect(() => {
    // If the program is continued, we don't need to add active merchant ids
    if (availableMerchants && !isContinued) {
      changeFlexActiveMerchantIds('flex', flexMerchantIds)
    }
  }, [availableMerchants])

  useEffect(() => {
    if (allSegments.length > 0) {
      const allEmployeeSegment = getAllEmployeeSegment(allSegments)
      getFlexRecommendations(allEmployeeSegment.id)
    }
  }, [allSegments])

  const monthlyGas = Math.min(
    Math.round(((flex.budget ? flex.budget : 0) / FlexUtils.monthlyGas) * 100),
    100
  )

  const handleTypeToggle = (type: flexActiveOptionsType) => {
    const newActive = !flex.activeOptions[type]
    changeFlexActiveOptions(type, newActive)
  }

  const handleProviderChange = (
    type: flexActiveOptionsType,
    newActiveMerchantIds: string[]
  ) => {
    changeFlexActiveMerchantIds(type, newActiveMerchantIds)
  }

  return (
    <>
      <ProgramEditingSubSection>
        <BudgetUsesSelector
          budget={flex.budget}
          onBudgetChange={(budget) => changeFlexBudget(budget * 100)}
          budgetSubtitle='Monthly Budget Per Commuter'
          showUses={false}
          budgetError={flex.budget <= 0}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramSectionSubHeading title='How often should the limit reset?' />
        <FlexContainer flexDirection='column' style={{ gap: '1rem' }}>
          <FlexContainer alignItems='center'>
            <Radio
              active={(flex.budgetCycle || 'monthly') === 'daily'}
              onClick={() => changeFlexBudgetCycle('daily')}
            />
            <Text variant='body1' marginLeft='0.5rem'>
              Daily
            </Text>
          </FlexContainer>
          <FlexContainer alignItems='center'>
            <Radio
              active={(flex.budgetCycle || 'monthly') === 'monthly'}
              onClick={() => changeFlexBudgetCycle('monthly')}
            />
            <Text variant='body1' marginLeft='0.5rem'>
              Monthly
            </Text>
          </FlexContainer>
        </FlexContainer>
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramOption
          type='flex'
          active={flex.activeOptions.flex}
          activeMerchantIds={flex.activeMerchantIds.flex}
          availableMerchantIds={flexMerchantIds}
          handleProviderChange={(newActiveMerchantIds) =>
            handleProviderChange('flex', newActiveMerchantIds)
          }
          handleToggle={() => handleTypeToggle('flex')}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection isLastSection>
        <ProgramOption
          type='gas'
          active={flex.activeOptions.gas}
          handleToggle={() => handleTypeToggle('gas')}
        >
          <ProgramPriceCoverage
            percentage={monthlyGas}
            title='Monthly Gas Price Covered'
            description={`Your budget will cover ${monthlyGas}% of the average monthly
                  gas spend (${FormattingUtils.formatDollar(
                    FlexUtils.monthlyGas
                  )}) for your commuters.`}
          />
        </ProgramOption>
        <br />
        <ProgramOption
          type='transit'
          active={flex.activeOptions.transit}
          handleToggle={() => handleTypeToggle('transit')}
        ></ProgramOption>
        <br />
        <ProgramOption
          type='parking'
          active={flex.activeOptions.parking}
          handleToggle={() => handleTypeToggle('parking')}
        ></ProgramOption>
      </ProgramEditingSubSection>
    </>
  )
}

export default React.memo(FlexProgramEditing)
